"use client";

import Image from "next/image";
import { useCallback, useEffect, useState } from "react";
import { IconArrowNarrowLeft, IconArrowNarrowRight, IconX } from "@tabler/icons-react";

export type LightboxImage = {
  src: string;
  alt?: string;
  title?: string;
};

type ImageLightboxProps = {
  images: LightboxImage[];
  initialIndex: number;
  isOpen: boolean;
  onClose: () => void;
  closeLabel: string;
  previousLabel: string;
  nextLabel: string;
  countLabel: (current: number, total: number) => string;
};

export const ImageLightbox = ({
  images,
  initialIndex,
  isOpen,
  onClose,
  closeLabel,
  previousLabel,
  nextLabel,
  countLabel,
}: ImageLightboxProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [prevInitialIndex, setPrevInitialIndex] = useState(initialIndex);
  // Reset the displayed image whenever the lightbox is reopened with a new starting index.
  // Adjusted during render (React's recommended pattern) instead of an effect, to avoid an extra render pass.
  if (initialIndex !== prevInitialIndex) {
    setPrevInitialIndex(initialIndex);
    setCurrentIndex(initialIndex);
  }
  const handlePrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);
  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  }, [images.length]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") handlePrevious();
      if (e.key === "ArrowRight") handleNext();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleNext, handlePrevious, isOpen, onClose]);

  if (!isOpen || images.length === 0) return null;

  const activeImage = images[currentIndex];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute right-4 top-4 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition-all hover:bg-white/20"
        aria-label={closeLabel}
      >
        <IconX className="h-6 w-6" />
      </button>

      <button
        onClick={(e) => {
          e.stopPropagation();
          handlePrevious();
        }}
        className="absolute left-4 top-1/2 z-50 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-all hover:bg-white/20"
        aria-label={previousLabel}
      >
        <IconArrowNarrowLeft className="h-6 w-6" />
      </button>

      <button
        onClick={(e) => {
          e.stopPropagation();
          handleNext();
        }}
        className="absolute right-4 top-1/2 z-50 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-all hover:bg-white/20"
        aria-label={nextLabel}
      >
        <IconArrowNarrowRight className="h-6 w-6" />
      </button>

      <div
        className="flex h-full w-full flex-col items-center justify-center p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative h-[70vh] w-full max-w-6xl">
          <Image
            src={activeImage.src}
            alt={activeImage.alt || activeImage.title || ""}
            fill
            className="object-contain"
            sizes="100vw"
            priority
          />
        </div>
        <div className="mt-4 text-center">
          <h3 className="text-xl font-semibold text-white">{activeImage.title}</h3>
          <p className="mt-1 text-sm text-gray-300">{countLabel(currentIndex + 1, images.length)}</p>
        </div>
      </div>
    </div>
  );
};
